import { Target, ThumbsUp, TriangleAlert } from "lucide-react";

import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// FitScoreAnalysis
//
// Displays the AI-generated analysis for a single recommendation:
//   • Fit score heading (score as a percentage)
//   • Why relevant   — emerald tinted panel
//   • Best use case  — blue tinted panel
//   • Potential risks — red tinted panel
//
// All four are optional; the component renders nothing at all if none are
// provided (matches the optional-block pattern used in OptionDetails).
// ---------------------------------------------------------------------------

export interface FitScoreAnalysisProps {
    fitScore?: number;
    whyRelevant?: string;
    bestUseCase?: string;
    potentialRisks?: string;
    className?: string;
}

export function FitScoreAnalysis({
    fitScore,
    whyRelevant,
    bestUseCase,
    potentialRisks,
    className,
}: FitScoreAnalysisProps) {
    if (fitScore == null && !whyRelevant && !bestUseCase && !potentialRisks) {
        return null;
    }

    return (
        <div className={cn("flex flex-col gap-3", className)}>
            <Separator className="mb-2" />
            {fitScore != null && (
                <h3 className="heading-3 text-zinc-900">
                    Fit score {Math.round(fitScore * 10)}%
                </h3>
            )}

            {whyRelevant && (
                <div className="flex flex-col gap-1.5 rounded-lg bg-emerald-50 px-4 py-3">
                    <h5 className="heading-5 flex items-center gap-1.5 text-emerald-900">
                        <ThumbsUp className="size-4 shrink-0" />
                        Why relevant
                    </h5>
                    <p className="pl-[22px] text-sm text-zinc-700 leading-body-sm">
                        {whyRelevant}
                    </p>
                </div>
            )}

            {bestUseCase && (
                <div className="flex flex-col gap-1.5 rounded-lg bg-blue-50 px-4 py-3">
                    <h5 className="heading-5 flex items-center gap-1.5 text-blue-900">
                        <Target className="size-4 shrink-0" />
                        Best use case
                    </h5>
                    <p className="pl-[22px] text-sm text-zinc-700 leading-body-sm">
                        {bestUseCase}
                    </p>
                </div>
            )}

            {potentialRisks && (
                <div className="flex flex-col gap-1.5 rounded-lg bg-red-50 px-4 py-3">
                    <h5 className="heading-5 flex items-center gap-1.5 text-red-900">
                        <TriangleAlert className="size-4 shrink-0" />
                        Potential risks
                    </h5>
                    <p className="pl-[22px] text-sm text-zinc-700 leading-body-sm">
                        {potentialRisks}
                    </p>
                </div>
            )}
        </div>
    );
}
